import firebase from "firebase";
import _ from "lodash";
import {replaceUndefinedWithDelete} from "../../../utils";
import {
    CreateAvailabilityData,
    FeAvailability,
    FsAvailability,
    UpdateAvailabilityInput,
    UpdateAvailabilityResult
} from "./types";

const availabilityDataConverter: firebase.firestore.FirestoreDataConverter<FeAvailability> = {
    fromFirestore(snapshot: firebase.firestore.QueryDocumentSnapshot<FsAvailability>): FeAvailability {
        const {createdAt, updatedAt, ...docData} = snapshot.data();
        return {
            id: snapshot.id,
            ...docData,
            createdAt: createdAt.toMillis(),
            updatedAt: updatedAt.toMillis()
        }
    },
    toFirestore({id, createdAt, updatedAt, ...docData}: FeAvailability): FsAvailability {
        return {
            ..._.pickBy(docData, _.identity) as FeAvailability,
            createdAt: firebase.firestore.Timestamp.fromMillis(createdAt),
            updatedAt: firebase.firestore.Timestamp.fromMillis(updatedAt),
        };
    }
}

export class AvailabilityService {
    private static GetCollection() {
        return firebase.firestore()
            .collection('availabilities')
            .withConverter(availabilityDataConverter);
    }

    async createAvailability(dto: CreateAvailabilityData): Promise<FeAvailability> {
        const dateInMillis = new Date().getTime();
        const result = await AvailabilityService.GetCollection().add({
            id: '',
            ...dto,
            createdAt: dateInMillis,
            updatedAt: dateInMillis,
        });
        return {
            ...dto,
            id: result.id,
            createdAt: dateInMillis,
            updatedAt: dateInMillis,
        }
    }

    async updateAvailability(id: string, dto: UpdateAvailabilityInput): Promise<UpdateAvailabilityResult> {
        await AvailabilityService.GetCollection().doc(id).update({
            ...replaceUndefinedWithDelete(dto),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return {
            id,
            updatedAt: new Date().getTime(),
            ...dto,
        }
    }

    async listAvailabilities(): Promise<FeAvailability[]> {
        const querySnapshots = await AvailabilityService.GetCollection().get();
        return querySnapshots.empty
            ? []
            : querySnapshots.docs.map(d => d.data())
    }

    async listClientAvailabilities(clientId: string): Promise<FeAvailability[]> {
        const querySnapshots = await AvailabilityService.GetCollection()
            .where('clientId', '==', clientId)
            .get();
        return querySnapshots.empty
            ? []
            : querySnapshots.docs.map(d => d.data())
    }

    async listWorkerAvailabilities(workerId: string, timeRange?: { start: number, end: number }): Promise<FeAvailability[]> {
        let querySnapshots;

        if (timeRange) {
            querySnapshots = await AvailabilityService.GetCollection()
                .where('workerId', '==', workerId)
                .where('from', '>=', timeRange.start)
                .where('from', '<=', timeRange.end)
                .get();
        } else {
            querySnapshots = await AvailabilityService.GetCollection()
                .where('workerId', '==', workerId)
                .get();
        }
        return querySnapshots.empty
            ? []
            : querySnapshots.docs.map(d => d.data())
    }

    async getAvailability(id: string): Promise<FeAvailability> {
        const snapshot = await AvailabilityService.GetCollection().doc(id).get();
        const worker = snapshot.data();
        if (!worker) {
            throw new Error('Availability not found');
        }
        return worker;
    }

     async deleteAvailability(id: string): Promise<string> {
        await AvailabilityService.GetCollection().doc(id).delete();
        return id;
    }
}



